import {
  defineConstProperty,
} from '@bingads-webui/reflection';

import stringify from 'json-stable-stringify';
import _ from 'underscore';
import Promise from 'bluebird';
import { CollectionCacheItem, EntityCacheItem } from './server-call-cache-item';
import { ObservableCache } from './observable-cache';
import { CacheEvent } from './cache-event';
import {
  createEntityCacheKey,
  entityCacheKeyMatchesId,
} from './entity-cache-key';
import type {
  EntityKey,
  ICacheItem,
  IEntityCacheItem,
  IOdataEntityResource,
  IOdataCollectionResource,
  ICacheDependants,
  IOdataPluginActions,
  ODataEntity,
  ODataOptions,
} from './types';

function shouldReuseCacheItem(item: ICacheItem | undefined, bypassCache: boolean): item is ICacheItem {
  return item && (!bypassCache || item.state.status === 'WAITING' || item.state.status === 'PENDING');
}

export function odataCachePlugin(
  edm: object,
  dependants: ICacheDependants,
  {
    fetchEntity,
    patchEntity,
    createEntity,
    deleteEntity,
    fetchCollection,
    action,
  }: IOdataPluginActions
) {
  const entityDeps = dependants || {};

  defineConstProperty(edm, 'odata.cache', (() => {
    // TODO: when collection cache is ready, use the response to fill up entity cache
    const collectionCache = new ObservableCache();
    const cacheEvent = new CacheEvent();
    function getCollection(
      collectionResource: IOdataCollectionResource,
      options: ODataOptions,
      hardResetCacheData: unknown,
      bypassCache: boolean
    ) {
      const typeName = collectionResource.type.elementType!.name;
      const { path } = collectionResource;
      const stringifiedOptions = stringify(options);
      const cacheKeyPath = [typeName, path, stringifiedOptions];
      const item = collectionCache.get(cacheKeyPath);
      if (shouldReuseCacheItem(item, bypassCache)) {
        if (hardResetCacheData) {
          item.setData(hardResetCacheData);
        }
        return item;
      }

      const newItem = new CollectionCacheItem({
        fetch: () => fetchCollection(collectionResource, options),
        afterSubscribe: cache => collectionCache.observerAdded(cache, cacheKeyPath),
        afterUnsubscribe: cache => collectionCache.observerReduced(cache, cacheKeyPath),
        hardResetCacheData,
      });
      collectionCache.set(cacheKeyPath, newItem);
      return newItem;
    }

    function collectionAction(
      collectionResource: IOdataCollectionResource,
      options: ODataOptions,
      bypassCache: boolean
    ) {
      const typeName = collectionResource.type.elementType!.name;
      const { path } = collectionResource;
      const stringifiedOptions = stringify(options);
      const cacheKeyPath = [typeName, path, stringifiedOptions];
      const item = collectionCache.get(cacheKeyPath);
      if (shouldReuseCacheItem(item, bypassCache)) {
        return item;
      }

      const newItem = new CollectionCacheItem({
        fetch: () => action(collectionResource, options),
        afterSubscribe: cache => collectionCache.observerAdded(cache, cacheKeyPath),
        afterUnsubscribe: cache => collectionCache.observerReduced(cache, cacheKeyPath),
      });
      collectionCache.set(cacheKeyPath, newItem);
      return newItem;
    }

    const entityCache = new ObservableCache();

    function invalidateCollectionCache(
      typeName: string,
      skipEntityCallPaths: string[] | null
    ) {
      collectionCache.invalidateObservedCache(typeName, skipEntityCallPaths);

      Object.keys(collectionCache.noObservers.entries).forEach((key) => {
        if (JSON.parse(key)[0] === typeName) {
          collectionCache.invalidateNoObserverCache(key);
        }
      });
    }

    function invalidateEntityCache(
      typeName: string,
      id: EntityKey | null,
      stringifiedOptions: string,
      skipEntityCallPaths: string[] | null,
      preservedEntityCacheKey: string | null
    ) {
      const forAll = id == null;

      if (entityCache.observedCache[typeName]) {
        if (forAll) {
          entityCache.invalidateObservedCache(typeName, skipEntityCallPaths);
        } else {
          entityCache.invalidateObservedCacheById(
            typeName,
            id,
            stringifiedOptions,
            skipEntityCallPaths,
            preservedEntityCacheKey
          );
        }
      }

      Object.keys(entityCache.noObservers.entries).forEach((key) => {
        const [type, entityCacheKey, options] = JSON.parse(key);
        const isPreservedEntry = preservedEntityCacheKey == null
          ? options === stringifiedOptions
          : entityCacheKey === preservedEntityCacheKey && options === stringifiedOptions;

        if (type === typeName
           && (forAll || (
             entityCacheKeyMatchesId(entityCacheKey, id)
             && !isPreservedEntry
           ))) {
          entityCache.invalidateNoObserverCache(key);
        }
      });
    }

    function invalidate(
      typeName: string,
      id: EntityKey | null,
      stringifiedOptions: string,
      context: Record<string, boolean>,
      skipEntityCallPaths: string[] | null = null,
      preservedEntityCacheKey: string | null = null
    ) {
      // eslint-disable-next-line no-param-reassign
      context[typeName] = true;
      invalidateCollectionCache(typeName, skipEntityCallPaths);
      invalidateEntityCache(
        typeName,
        id,
        stringifiedOptions,
        skipEntityCallPaths,
        preservedEntityCacheKey
      );

      _.each(entityDeps[typeName], (type: string) => {
        if (!context[type]) {
          // eslint-disable-next-line no-param-reassign
          context[type] = true;
          invalidate(type, null, '', context, skipEntityCallPaths);
        }
      });
      cacheEvent.notify('invalidate', typeName);
    }

    function invalidateTypeCache(typeName: string, id: EntityKey | null = null, stringifiedOptions: string = '', skipEntityCallPaths: string[] | null = null) {
      invalidate(typeName, id, stringifiedOptions, {}, skipEntityCallPaths);
    }

    function getEntity(
      resource: IOdataEntityResource,
      options: ODataOptions,
      hardResetCacheData: unknown,
      bypassCache: boolean
    ) {
      const typeName = resource.type.name;
      const idOrPath = resource.navigation.key ?? resource.path;
      const entityCacheKey = createEntityCacheKey(resource);
      const stringifiedOptions = stringify(options);
      const cacheKeyPath = [typeName, entityCacheKey, stringifiedOptions];
      const item = entityCache.get(cacheKeyPath) as IEntityCacheItem | undefined;
      if (shouldReuseCacheItem(item, bypassCache)) {
        if (hardResetCacheData) {
          item.setData(hardResetCacheData);
        }
        return item;
      }
      const newItem = new EntityCacheItem({
        fetch: () => fetchEntity(resource, options),
        patch: (update: ODataEntity) => patchEntity(resource, options, update)
          .tap(() => invalidate(
            typeName,
            idOrPath,
            stringifiedOptions,
            {},
            null,
            entityCacheKey
          )),
        afterSubscribe: cache => entityCache.observerAdded(cache, cacheKeyPath),
        afterUnsubscribe: cache => entityCache.observerReduced(cache, cacheKeyPath),
        hardResetCacheData,
      });
      entityCache.set(cacheKeyPath, newItem);
      return newItem;
    }

    function entityAction(resource: IOdataEntityResource, options: ODataOptions, bypassCache: boolean) {
      const typeName = resource.type.name;
      const { path } = resource.navigation;
      const stringifiedOptions = stringify(options);
      const cacheKeyPath = [typeName, path, stringifiedOptions];
      const item = entityCache.get(cacheKeyPath);
      if (shouldReuseCacheItem(item, bypassCache)) {
        return item;
      }
      const newItem = new EntityCacheItem({
        fetch: () => action(resource, options),
        afterSubscribe: cache => entityCache.observerAdded(cache, cacheKeyPath),
        afterUnsubscribe: cache => entityCache.observerReduced(cache, cacheKeyPath),
      });
      entityCache.set(cacheKeyPath, newItem);
      return newItem;
    }

    function postEntity(collectionResource: IOdataCollectionResource, options: ODataOptions, data: ODataEntity) {
      const stringifiedOptions = stringify(options);
      const entityPromise = createEntity(collectionResource, options, data);
      const keyProperty = collectionResource.type.elementType!.key;
      const typeName = collectionResource.type.elementType!.name;
      // Some OData create endpoints return the full new entity ({ Id, ... }),
      // others return only the key envelope ({ value: <newId> }). Fall back to
      // `value` so the new entity's key is always defined. If it were left
      // `undefined`, the post-create `invalidateTypeCache` below would run with
      // `id = undefined`, which `invalidateEntityCache` treats as "invalidate
      // ALL" (forAll) — re-fetching every observed entity of this type by key
      // (a GET that some middle tiers don't even support for the entity set).
      const resourcePromise = entityPromise
        .then(entity => collectionResource.$withKey!((entity[keyProperty] ?? entity.value) as EntityKey));
      let fetchFirstCalledAndPatchNotCalled = true;
      const newItem = new EntityCacheItem({
        fetch: () => {
          if (fetchFirstCalledAndPatchNotCalled) {
            fetchFirstCalledAndPatchNotCalled = false;
            return entityPromise;
          }
          return resourcePromise.then((resource: IOdataEntityResource) => fetchEntity(resource, options));
        },
        patch: (update: ODataEntity) => {
          fetchFirstCalledAndPatchNotCalled = false;
          return resourcePromise
            .tap((resource: IOdataEntityResource) => invalidate(
              typeName,
              resource.navigation.key!,
              stringifiedOptions,
              {},
              null,
              createEntityCacheKey(resource)
            ))
            .then((resource: IOdataEntityResource) => patchEntity(resource, options, update));
        },
      });
      resourcePromise
        .then((resource: IOdataEntityResource) => {
          const id = resource.navigation.key!;
          const entityCacheKey = createEntityCacheKey(resource);
          const cacheKeyPath = [typeName, entityCacheKey, stringifiedOptions];

          newItem.afterSubscribe = cache => entityCache.observerAdded(cache, cacheKeyPath);
          newItem.afterUnsubscribe = cache => entityCache.observerReduced(cache, cacheKeyPath);
          entityCache.set(cacheKeyPath, newItem);
          invalidate(typeName, id, stringifiedOptions, {}, null, entityCacheKey);
        })
        // This is a fire-and-forget SIDE-EFFECT branch: it only populates the
        // entity cache from the newly-created entity's id, and its result is
        // not returned or awaited. On a failed create there is no new entity,
        // so the setup is skipped and this branch just rejects.
        //
        // The create failure is propagated to the caller on a SEPARATE branch —
        // `newItem.fetch()` returns `entityPromise`, which `CallObservable`
        // pushes to its observers on error (→ `toPromise()` rejects). So the
        // rejection here is a duplicate of an already-delivered error; swallow
        // it so it isn't flagged as an unhandled bluebird rejection. Re-throwing
        // would NOT reach the caller (different branch) and would simply re-float
        // as unhandled.
        .catch(_.noop);
      return newItem;
    }

    function destroyEntityAsPromise(resource: IOdataEntityResource) {
      const typeName = resource.type.name;
      const id = resource.navigation.key!;

      // NOTE: likezh: for delete, since the effect of deletion will be an entity being removed,
      // it is not useful to keep the call in cache. Hence we're directly returning a Promise here.
      return new Promise<void>((resolve, reject) => {
        deleteEntity(resource)
          .then(() => {
            invalidateTypeCache(typeName, id, '');
            resolve();
          })
          .catch((err: unknown) => reject(err));
      });
    }

    return {
      getEntity,
      postEntity,
      destroyEntityAsPromise,
      entityAction,
      getCollection,
      collectionAction,
      invalidateTypeCache,
      cacheEvent,
    };
  })());
}
